import React, { useRef } from "react";
import styled, { css } from "styled-components";
import {
  Button,
  PageTitle,
  PageWrapper,
  Section,
  Spinner,
  Table,
} from "@cinesys/ui/components";
import { Download, FileText, Upload } from "react-feather";
import { useQuery } from "react-query";
import ApiService from "../../services/ApiService";
import useJsonSlot from "../configurePluginOrScript/useJsonSlot";

interface JsonSlotListEntry {
  key: string;
  title: string;
  description: string;
  pluginId: string;
  pluginTitle: string;
  filename: string;
  updatedAt: string | null;
  updatedBy: string | null;
}

const DocInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const PluginName = styled.div`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--gray100);
`;

const Title = styled.div`
  font-weight: 600;
  font-size: 15px;
  color: var(--gray500);
`;

const Description = styled.div`
  color: var(--gray300);
  font-size: 13px;
  font-style: italic;
  line-height: 1.4;
  margin-top: 1px;
`;

const FileMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  align-items: flex-start;
`;

const FileBadge = styled.div<{ $uploaded: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 8px;
  border-radius: 6px;
  font-size: 12.5px;
  max-width: 100%;
  background-color: var(--dark300);
  border: 1px solid var(--dark400);
  color: var(--gray300);

  ${(props) =>
    props.$uploaded &&
    css`
      background-color: var(--greenBackground);
      border-color: var(--greenBorder);
      color: var(--green400);
    `}

  > svg {
    flex-shrink: 0;
  }
`;

const Filename = styled.span`
  font-family: "Source Code Pro", monospace;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const UpdatedMeta = styled.div`
  font-size: 12px;
  color: var(--dark700);
`;

const Error = styled.div`
  color: var(--red300);
  font-size: 13px;
`;

const Buttons = styled.div`
  display: flex;
  gap: 8px;
`;

/**
 * "way too hidden" (Addison, reacting to the now-removed per-plugin-only JsonSlotCard) — every
 * declared JSON slot across every plugin, flat, with download/upload right in the row. Same
 * upload/download logic the old per-plugin card used (`useJsonSlot`, shared), just a different
 * list chrome around it. Now the only entry point for JSON slots — the per-plugin Definition
 * viewer card was removed (Monday 12676293721).
 */
const DocumentRow = ({ slot }: { slot: JsonSlotListEntry }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  // Seed with the bulk fetch's own filename/updatedAt/updatedBy so this doesn't re-issue the
  // exact per-slot GET the bulk endpoint just made redundant (see useJsonSlot's doc comment).
  const { data, isLoading, download, upload, isUploading, uploadError } =
    useJsonSlot(slot.key, {
      filename: slot.filename,
      updatedAt: slot.updatedAt,
      updatedBy: slot.updatedBy,
    });

  return (
    <>
      <div>
        <DocInfo>
          <PluginName>{slot.pluginTitle}</PluginName>
          <Title>{slot.title}</Title>
          {slot.description && <Description>{slot.description}</Description>}
        </DocInfo>
      </div>
      <div>
        {isLoading || !data ? (
          <Spinner />
        ) : (
          <>
            <FileMeta>
              <FileBadge $uploaded={!!data.updatedAt}>
                <FileText size={13} />
                <Filename>{data.filename}</Filename>
              </FileBadge>
              <UpdatedMeta>
                {data.updatedAt
                  ? `Updated ${new Date(data.updatedAt).toLocaleString()}${
                      data.updatedBy ? ` by ${data.updatedBy}` : ""
                    }`
                  : "Not uploaded yet"}
              </UpdatedMeta>
            </FileMeta>
            {uploadError && <Error>{uploadError}</Error>}
          </>
        )}
      </div>
      <Buttons>
        <Button
          type="default"
          label="Download"
          icon={Download}
          onClick={download}
        />
        <Button
          type="default"
          label="Upload"
          icon={Upload}
          onClick={() => inputRef.current?.click()}
          loading={isUploading}
        />
        <input
          ref={inputRef}
          type="file"
          accept="application/json"
          hidden
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) {
              upload(file);
            }
            e.target.value = "";
          }}
        />
      </Buttons>
    </>
  );
};

const ConfigDocumentsListScreen = () => {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["jsonSlots"],
    queryFn: async () => {
      const response = await ApiService.fetch<{ data: JsonSlotListEntry[] }>(
        "GET",
        "/api/config/json-slots"
      );
      return response.data;
    },
  });

  const rows = [...(data ?? [])].sort((a, b) => a.title.localeCompare(b.title));

  const columns = [
    { title: "Document", width: "1fr" },
    { title: "Current file", width: "1fr" },
    { width: "220px" },
  ];

  return (
    <div>
      <PageTitle
        title="Documents"
        error={isError ? "Problem loading documents" : undefined}
      />
      {isLoading ? (
        <Spinner />
      ) : (
        !isError && (
          <PageWrapper>
            <Section pad={rows.length === 0}>
              {rows.length === 0 ? (
                "No documents declared by any plugin yet."
              ) : (
                <Table
                  style={
                    {
                      "--tableHeaderBg": "transparent",
                      "--tableHeaderBorderBottom": "1px solid var(--dark300)",
                      "--tableHeaderShadow": "none",
                    } as React.CSSProperties
                  }
                  columnGrid
                  columns={columns}
                  rows={rows.map((slot) => ({
                    id: slot.key,
                    element: () => <DocumentRow slot={slot} />,
                  }))}
                  autoHeight={{}}
                />
              )}
            </Section>
          </PageWrapper>
        )
      )}
    </div>
  );
};

export default ConfigDocumentsListScreen;
